import React from 'react';
import { Link } from 'react-router-dom';

const LAST_UPDATED = 'October 15, 2026';

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="text-xl font-semibold text-foreground mb-3">{title}</h2>
      <div className="space-y-3 text-foreground/90 leading-relaxed">{children}</div>
    </section>
  );
}

function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 py-10 md:py-16 pb-20 md:pb-16">
        <h1 className="text-3xl font-bold text-foreground mb-2">Terms of Service</h1>
        <p className="text-sm text-muted-foreground mb-8">Last updated: {LAST_UPDATED}</p>

        <div className="bg-secondary/50 border border-border rounded-lg p-4 mb-8">
          <p className="text-sm text-foreground">
            <strong>Beta Notice:</strong> Laftel Condo is currently offered as a beta product.
            It may contain bugs, errors, or interruptions in service, and features may change
            or be removed without notice. By using the service you acknowledge and accept this.
          </p>
        </div>

        <Section title="1. Acceptance of Terms">
          <p>
            These Terms of Service ("Terms") govern your access to and use of Laftel Condo
            (the "Service"), operated by Laftel Inc. ("Laftel", "we", "us", or "our"). By creating
            an account or otherwise using the Service, you agree to be bound by these Terms and
            by our <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.
            If you do not agree, do not use the Service.
          </p>
        </Section>

        <Section title="2. Description of the Service">
          <p>
            Laftel Condo is a software platform intended to help residents, owners, tenants,
            council members, and property managers of a residential building or condominium
            communicate with one another and stay informed. The Service provides features such
            as bulletins/announcements, a community forum, a maintenance ticket system, a shared
            calendar, a building handbook, and amenity booking.
          </p>
          <p>
            Laftel is a software provider only. We do not own, operate, manage, or govern any
            condominium, strata corporation, homeowners' association, building, or amenity. We
            are not a property manager, condo board, strata council, or building staff member,
            and nothing in the Service should be interpreted as advice or action taken on behalf
            of any such body.
          </p>
        </Section>

        <Section title="3. Accounts and Eligibility">
          <p>
            To use most features of the Service you must register for an account and be
            associated with a unit or role within a participating condo community, subject to
            approval by that community's administrator(s). You are responsible for maintaining
            the confidentiality of your login credentials and for all activity that occurs under
            your account. You must provide accurate information when registering.
          </p>
        </Section>

        <Section title="4. User Content and Conduct">
          <p>
            Bulletins, forum posts and comments, tickets, ticket updates, and any other content
            you submit through the Service ("User Content") are created and posted by users and
            condo administrators, not by Laftel. We do not review, verify, or endorse User
            Content before it is published, and we are not responsible for its accuracy,
            legality, or appropriateness.
          </p>
          <p>
            You agree not to post User Content that is unlawful, harassing, defamatory,
            fraudulent, or that violates the rights of any third party. You are solely
            responsible for the content you submit and for your interactions with other users.
            We may, but are not obligated to, remove content or suspend accounts that violate
            these Terms.
          </p>
        </Section>

        <Section title="5. Amenity Bookings">
          <p>
            The Service allows residents to reserve shared amenities (such as party rooms,
            gyms, or guest suites) that belong to the condo community, not to Laftel. Laftel
            does not own, maintain, inspect, insure, or supervise any amenity, and has no control
            over its physical condition, availability, or safety. Any injury, loss, or damage
            arising from the use of a booked amenity is a matter between you and the condo
            community/building management — not Laftel.
          </p>
        </Section>

        <Section title="6. No Responsibility for Condo Matters">
          <p>
            Laftel is not responsible or liable for, and expressly disclaims involvement in:
            the maintenance or physical condition of any building or unit; decisions, actions,
            or omissions of condo boards, strata councils, property managers, or agents; disputes
            between residents, owners, tenants, or condo administrators; the handling or
            resolution of maintenance tickets submitted through the Service; or any harm, injury,
            financial loss, or property damage occurring within or around a condo community.
          </p>
          <p>
            The Service is a communication and coordination tool only. Use of the Service does
            not create any obligation on Laftel to act, respond to, resolve, or guarantee the
            outcome of any ticket, bulletin, booking, or forum discussion.
          </p>
        </Section>

        <Section title="7. Disclaimer of Warranties">
          <p>
            The Service is provided "as is" and "as available," without warranties of any kind,
            whether express or implied, including but not limited to implied warranties of
            merchantability, fitness for a particular purpose, and non-infringement. As a beta
            product, the Service may experience downtime, data loss, bugs, or incomplete features.
            We do not warrant that the Service will be uninterrupted, secure, timely, or error-free,
            or that any notification (including maintenance, safety, or emergency-related bulletins)
            will be delivered or delivered on time.
          </p>
        </Section>

        <Section title="8. Limitation of Liability">
          <p>
            To the fullest extent permitted by applicable law, Laftel Inc. and its owners,
            directors, officers, employees, contractors, agents, affiliates, and licensors shall
            have no liability whatsoever for any claim, damage, loss, cost, expense, injury,
            death, property damage, data loss, loss of revenue, or other liability of any kind,
            whether direct, indirect, incidental, special, consequential, exemplary, or punitive,
            arising out of or related to the Service; your use of or inability to use the Service;
            any User Content, amenity booking, or notification; or any condo-related matter,
            regardless of the legal theory asserted and even if advised of the possibility of
            such harm. Where applicable law does not permit a complete exclusion of liability,
            liability is excluded to the maximum extent permitted by law.
          </p>
        </Section>

        <Section title="9. Indemnification">
          <p>
            You agree to indemnify and hold harmless Laftel from any claims, damages, losses, or
            expenses (including reasonable legal fees) arising out of your use of the Service,
            your User Content, your violation of these Terms, or your violation of any rights of
            a third party, including other residents or the condo community.
          </p>
        </Section>

        <Section title="10. Third-Party Services">
          <p>
            The Service relies on third-party providers to function, including email delivery
            (Mailgun) and AI-assisted content processing (OpenAI). We are not responsible for
            outages, delays, or errors caused by these third-party providers.
          </p>
        </Section>

        <Section title="11. Termination">
          <p>
            We may suspend or terminate your access to the Service at any time, with or without
            notice, including for violation of these Terms. You may stop using the Service and
            request account deletion at any time.
          </p>
        </Section>

        <Section title="12. Changes to These Terms">
          <p>
            We may update these Terms from time to time, particularly as the Service evolves out
            of beta. Continued use of the Service after changes are posted constitutes acceptance
            of the updated Terms.
          </p>
        </Section>

        <Section title="13. Contact">
          <p>
            Questions about these Terms can be sent to{' '}
            <a href="mailto:legal@laftel.io" className="text-primary hover:underline">legal@laftel.io</a>.
          </p>
        </Section>

        <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
          ← Back to Home
        </Link>
      </div>
    </div>
  );
}

export default TermsOfServicePage;
